import { makeStyles } from '@material-ui/core/styles';
import { Handle, Position } from '@xyflow/react';
import classnames from 'classnames';
import hexToRgba from 'hex-rgba';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../app/theme';
import IconEnter from '../../assets/images/Icons/IconEnter';
import icUser from '../../assets/images/svg/ic_user.svg';
import icUserNotAllow from '../../assets/images/svg/ic_user_notAllow.svg';
import {
  checkNode,
  setDefaultTemplate,
  setIsShowSettingBar,
  setSelectedNode,
} from '../../features/mainSlice';

const useStyles = makeStyles(
  {
    root: {
      width: 240,
      borderRadius: '12px',
      boxShadow: '0 8px 36px 0 rgba(0, 0, 0, 0.06)',
      border: 0,
    },
    selected: {
      border: `solid 1px ${color.$15}`,
    },
    error: {
      border: `solid 1px ${color.$17}!important`,
    },
    highlightPath: {
      border: `solid 1px ${color.$3}!important`,
    },
    handle: {
      border: '0 !important',
      borderRadius: '0 !important',
      background: `${color.$3} !important`,
      width: '1px !important',
      height: '1px !important',
      minWidth: '1px !important',
      minHeight: '1px !important',
    },
    title: {
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
      fontSize: '13px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$15,
      display: 'flex',
      alignItems: 'center',
      padding: '8px 16px',
      background: hexToRgba(color.$15, 6),
      '& > svg': {
        marginRight: 2,
      },
    },
    errorTitle: {
      color: color.$17,
      background: hexToRgba(color.$17, 6),
    },
    content: {
      padding: '12px 16px',
    },
    condition: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1.38',
      letterSpacing: 'normal',
      color: color.$3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px 0',
      background: color.$19,
      '& > img': {
        marginRight: 4,
      },
    },
  },
  {
    name: 'CustomerCondition',
  },
);

const CustomerConditionComponent = ({ id, data, isConnectable }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isErrorNodeIds = useSelector(store => store.ma.isErrorNodeIds);
  const selectedNode = useSelector(store => store.ma.selectedNode);
  const nodes = useSelector(store => store.ma.data?.nodes) || [];
  const allPathNodes = useSelector(store => store.ma.highlightPathNodes) || [];
  const isPreview = useSelector(store => store.ma.isPreview);

  const targetNode = useMemo(
    () => nodes.find(n => n?.id === id) || {},
    [nodes],
  );

  const groupNode = useMemo(
    () => nodes.find(n => n?.id === data?.groupId) || {},
    [nodes],
  );

  const { triggerType } = groupNode?.data || {};

  const contentImg = useMemo(() => {
    switch (data?.type) {
      case 'trigger':
        return icUser;
      case 'n-trigger':
      default:
        return icUserNotAllow;
    }
  }, [data?.type]);

  const isHighlightPath = isPreview && !!allPathNodes.find(n => n?.id === id);
  const isSelected = selectedNode?.id === data?.groupId;
  const isError =
    !!~isErrorNodeIds.indexOf(data?.groupId) && !checkNode(groupNode);

  const onSelect = () => {
    if (!isSelected && groupNode) {
      dispatch(setSelectedNode(isPreview ? targetNode : groupNode));
      dispatch(
        setDefaultTemplate({
          isShow: false,
        }),
      );
    }

    dispatch(setIsShowSettingBar(true));
  };

  return (
    <div>
      <Handle
        className={classes.handle}
        type="target"
        position={Position.Top}
        style={{ top: 0 }}
        isConnectable={isConnectable}
      />
      <div
        className={classnames(classes.root, {
          [classes.selected]: isSelected,
          [classes.error]: isError,
          [classes.highlightPath]: isHighlightPath,
        })}
        onClick={onSelect}
      >
        <div
          className={classnames(classes.title, {
            [classes.errorTitle]: isError,
          })}
        >
          <IconEnter backgroundColor={isError ? color.$17 : color.$15} />
          {t('add-customerConditions')}
        </div>
        <div className={classes.content}>
          <div className={classes.condition}>
            <img src={contentImg} alt="click" />
            {t(`customerConditionNode.${data?.type}`, {
              triggerType: t(`customerConditionNode.${triggerType}`),
            })}
          </div>
        </div>
      </div>
      <Handle
        className={classes.handle}
        type="source"
        position={Position.Bottom}
        style={{ bottom: 0 }}
        isConnectable={isConnectable}
      />
    </div>
  );
};

export default CustomerConditionComponent;
